import { defineComponent } from 'vue'
import ballRed from '@/images/ball-red.png'
import ballGreen from '@/images/ball-green.png'
import ballBlue from '@/images/ball-blue.png'
import ballGray from '@/images/ball-gray.png'
import { Icon } from '@iconify/vue'
import { Row as VanRow, Col as VanCol } from 'vant'
import { getApiV1LotteryDetail49208 } from '@/api'
import type { LotteryNumberItem } from '@/api'
import { getBallAttributes, getZodiacYear, parseLotteryDate } from '@/utils/lottery-attr'

// ==================== 类型 ====================

export type BallColor = 'red' | 'blue' | 'green' | 'gray'

export interface LotteryBall {
  // 正常情况下为 number（已 parseInt），开奖直播进行中时后端返回中文字符，此时保留原始 string
  number: number | string
  color: BallColor
  zodiac: string
  wuxing: string
}

// ==================== 常量 ====================

// 保留导出以兼容可能的外部引用
export const BALL_BORDER_CLASS: Record<BallColor, string> = {
  red: 'border-[#EE3333]',
  blue: 'border-[#44AAFF]',
  green: 'border-[#22CC55]',
  gray: 'border-[#999999]',
}

const BALL_IMAGE: Record<BallColor, string> = {
  red: ballRed,
  blue: ballBlue,
  green: ballGreen,
  gray: ballGray,
}

// ==================== 内部组件 ====================

const BallItem = defineComponent({
  props: {
    number: { type: [Number, String] as unknown as () => number | string, required: true },
    color: { type: String as () => BallColor, required: true },
  },
  setup(props) {
    const replaceCharer = (str: string) => {
      return str
        .replace('四', '彩')
        .replace('九', '票')
        .replace('图', '助')
        .replace('库', '手')
    }
    return () => (
      <div class="relative w-full aspect-square shrink-0 select-none">
        <img src={BALL_IMAGE[props.color]} alt="" class="absolute inset-0 w-full h-full" />
        <span class="absolute inset-0 flex items-center justify-center text-[.4rem] font-600 z-10">
          {/* 非数字字符串（开奖直播中）直接显示；数字类型补零到两位 */}
          {typeof props.number === 'string' && !/^\d+$/.test(props.number)
            ? replaceCharer(props.number)
            : String(props.number).padStart(2, '0')}
        </span>
      </div>
    )
  },
})

// ==================== 辅助方法 ====================

/** 当 useAlgorithm 开启时，用本地算法覆盖球的 color/zodiac/wuxing */
function resolveBall(ball: LotteryBall, zodiacYear: number | null): LotteryBall {
  if (zodiacYear === null) return ball
  const num = typeof ball.number === 'number' ? ball.number : Number.parseInt(ball.number, 10)
  if (Number.isNaN(num) || num < 1 || num > 49) return ball
  const attrs = getBallAttributes(num, zodiacYear)
  return { ...ball, ...attrs }
}

/**
 * 平码排序比较：两个号码都是纯数字时按大小排（order 为 asc 升序，否则降序）；
 * 有一方不是数字（如未开出的占位）时视为相等，保持原有先后。
 */
export function compareBallNumbers(a: LotteryBall, b: LotteryBall, order: string): number {
  if (!/^\d+$/.test(String(a.number)) || !/^\d+$/.test(String(b.number))) return 0
  return order === 'asc' ? Number(a.number) - Number(b.number) : Number(b.number) - Number(a.number)
}

// ==================== 开奖号码（49208 接口 numberList → 球） ====================

/** 一期开奖：6 个正码 + 1 个特码 */
export interface DrawBalls {
  balls: LotteryBall[]
  specialBall: LotteryBall
}

const COLOR_NUM_MAP: Record<number, Exclude<BallColor, 'gray'>> = {
  1: 'red',
  2: 'blue',
  3: 'green',
}

/** 接口波色编号（1 红 / 2 蓝 / 3 绿）→ 波色，未知按红 */
export function mapColorNum(c: number): Exclude<BallColor, 'gray'> {
  return COLOR_NUM_MAP[c] ?? 'red'
}

/** 波色 → 49208 球底图 class 序号（.bg-amage-N） */
export const BALL_AMAGE_MAP: Record<BallColor, number> = {
  red: 1,
  blue: 2,
  green: 3,
  gray: 0,
}

/** 号码补零到两位；开奖直播中的非数字字符串原样返回 */
export function padBallNumber(n: number | string): string {
  return typeof n === 'string' && !/^\d+$/.test(n) ? n : String(n).padStart(2, '0')
}

function toBall(n: LotteryNumberItem): LotteryBall {
  return {
    number: Number.parseInt(n.number ?? '0'),
    color: mapColorNum(n.color ?? 0),
    zodiac: n.shengXiao ?? '',
    wuxing: n.wuXing ?? '',
  }
}

/** numberList 前 6 个是正码、第 7 个是特码；没有特码时用 0 号红球占位 */
export function toDrawBalls(numbers: LotteryNumberItem[]): DrawBalls {
  const special = numbers[6]
  return {
    balls: numbers.slice(0, 6).map(toBall),
    specialBall: special ? toBall(special) : { number: 0, color: 'red', zodiac: '', wuxing: '' },
  }
}

/** 按开奖记录 id 取一期开奖号码，没有记录时返回 null */
export async function fetchDrawBalls(region: 'hk' | 'aocai', id: number): Promise<DrawBalls | null> {
  const res = await getApiV1LotteryDetail49208({ query: { region, id } })
  const record = res.data?.data?.record
  return record ? toDrawBalls(record.numberList ?? []) : null
}

/** 正码与特码之间的「+」号（开奖记录列表、开奖详情） */
export const PlusSvg = defineComponent({
  setup() {
    return () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="12"
        viewBox="0 0 13 12"
        fill="none"
      >
        <path d="M1.42856 6H11.4286" stroke="#999" stroke-width="2" stroke-linecap="round" />
        <path d="M6.42856 1V11" stroke="#999" stroke-width="2" stroke-linecap="round" />
      </svg>
    )
  },
})

function renderAmageBall(ball: LotteryBall) {
  return (
    <div class={`icon-text bg-amage-${BALL_AMAGE_MAP[ball.color]}`}>
      <div>
        <span class="slot-icon">
          <span>{String(ball.number).padStart(2, '0')}</span>
        </span>
      </div>
      <div class="slot-text">
        {ball.zodiac}
        {ball.wuxing ? <span>/{ball.wuxing}</span> : null}
      </div>
    </div>
  )
}

/** 正码位置固定 6 格；开奖中号码会变、未开出时为空，列表用位置名做 key */
export const REGULAR_SLOTS = ['正一', '正二', '正三', '正四', '正五', '正六']

function renderRecordBall(n: LotteryNumberItem) {
  return (
    <div class={['icon-text', `bg-amage-${n.color ?? 0}`]}>
      <span class="slot-icon">
        <span>{n.number || '--'}</span>
      </span>
      <div class="slot-text">
        {n.shengXiao}
        {n.wuXing ? <span>/{n.wuXing}</span> : null}
      </div>
    </div>
  )
}

/** 资料 / 帖子详情里的开奖号码行：直接用接口 numberList，未开出的号码显示 -- */
export function renderRecordBalls(numberList: LotteryNumberItem[]) {
  const special = numberList[6]
  return (
    <div class="van-row">
      {numberList.slice(0, 6).map((n, i) => (
        <div class="van-col tp left" key={REGULAR_SLOTS[i]}>
          {renderRecordBall(n)}
        </div>
      ))}
      <div class="van-col tc">
        <div class="connect">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="12"
            viewBox="0 0 13 12"
            fill="none"
          >
            <path
              d="M1.42856 6H11.4286"
              stroke="#999"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M6.42856 1V11"
              stroke="#999"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
      {special && <div class="van-col tp right">{renderRecordBall(special)}</div>}
    </div>
  )
}

/** 带年/期标题（默认插槽）的开奖球区（49208 .border-box.testt），图库详情、投票记录、竞猜页共用 */
export const DrawBallsBox = defineComponent({
  props: {
    draw: { type: Object as () => DrawBalls, required: true },
  },
  setup(props, { slots }) {
    return () => (
      <div class="border-box testt">
        <div class="border-box-body">
          <div class="body-hd-2">{slots.default?.()}</div>
          <div class="border-box-body-bd">
            <VanRow>
              {/* 同一期的号码互不重复，直接用号码做 key */}
              {props.draw.balls.map((ball) => (
                <VanCol key={ball.number} class="tp left">
                  {renderAmageBall(ball)}
                </VanCol>
              ))}
              <VanCol class="tc">
                <div class="connect">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                    <path d="M1.42856 6H11.4286" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M6.42856 1V11" stroke="#999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
              </VanCol>
              <VanCol class="tp right">{renderAmageBall(props.draw.specialBall)}</VanCol>
            </VanRow>
          </div>
        </div>
      </div>
    )
  },
})

// ==================== 导出组件 ====================

export const LotteryBallRow = defineComponent({
  props: {
    balls: { type: Array as () => LotteryBall[], required: true },
    specialBall: { type: Object as () => LotteryBall, required: true },
    showWuxing: { type: Boolean, default: true },
    ballsOrder: {
      type: String as () => 'original' | 'asc' | 'desc',
      default: 'original',
    },
    /** 是否使用本地算法计算波色/生肖/五行（默认 false，使用 API 数据） */
    useAlgorithm: { type: Boolean, default: false },
    /** 开奖日期字符串，useAlgorithm=true 时必填，用于计算生肖年份 */
    lotteryDate: { type: String, default: '' },
  },
  setup(props) {
    return () => {
      const zodiacYear =
        props.useAlgorithm && props.lotteryDate
          ? getZodiacYear(parseLotteryDate(props.lotteryDate))
          : null

      const resolvedBalls = props.balls.map((b) => resolveBall(b, zodiacYear))
      const resolvedSpecial = resolveBall(props.specialBall, zodiacYear)

      const sortedBalls =
        props.ballsOrder === 'original'
          ? resolvedBalls
          : [...resolvedBalls].sort((a, b) => compareBallNumbers(a, b, props.ballsOrder))

      return (
        <div class="grid grid-cols-22 gap-[.04rem]">
          {/* 开奖直播中号码是占位文字、可能重复，key 用「号码 + 位置」 */}
          {sortedBalls.map((ball, i) => (
            <div key={`${ball.number}-${i}`} class="flex flex-col items-center gap-[.04rem] col-span-3">
              <BallItem number={ball.number} color={ball.color} />
              <span class="text-[.24rem] text-[#333]">
                {ball.zodiac}
                {props.showWuxing ? `/${ball.wuxing}` : ''}
              </span>
            </div>
          ))}
          {/* + 号垂直居中对齐球体 */}
          <div class="flex flex-col gap-[.04rem] items-center justify-center col-span-1">
            <div class="relative h-full w-full">
              <Icon
                icon="mdi:plus"
                class="text-[#999] absolute top-50% left-50% transform -translate-x-1/2 -translate-y-1/2 w-[.3rem] h-[.3rem]"
              />
            </div>
            <span data-name="text-placeholder" class="text-[.2rem]">
              &nbsp;
            </span>
          </div>
          <div class="flex flex-col items-center gap-[.04rem] col-span-3">
            <BallItem number={resolvedSpecial.number} color={resolvedSpecial.color} />
            <span class="text-[.24rem] text-[#555]">
              {resolvedSpecial.zodiac}
              {props.showWuxing ? `/${resolvedSpecial.wuxing}` : ''}
            </span>
          </div>
        </div>
      )
    }
  },
})
